import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  Chip,
  IconButton,
  Fade,
  Slide,
  Grow,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  Avatar,
} from '@mui/material';
import {
  MedicalServices,
  AttachMoney,
  Assignment,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  CheckCircle,
  PendingActions,
  TrendingUp,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { bookingsService } from './bookings/bookings.service';
import { Booking } from './bookings/bookings.dto';
import { BOOKING_STATUS, REPORT_STATUS, DEFAULT_CURRENCY } from '@/constants';
import { useNavigate } from 'react-router-dom';

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactElement;
  color: string;
  trend?: string;
  index: number;
}

const StatCard = ({
  title,
  value,
  icon,
  color,
  trend,
  index,
}: StatCardProps) => (
  <Grow in timeout={800 + index * 200}>
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.2)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box>
            <Typography
              color="text.secondary"
              gutterBottom
              variant="body2"
              fontWeight="500"
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              fontWeight="bold"
              color="text.primary"
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: `${color}15`,
              borderRadius: 3,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>

        {trend && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              {trend.startsWith('+') ? (
                <ArrowUpward
                  sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }}
                />
              ) : (
                <ArrowDownward
                  sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }}
                />
              )}
              <Typography
                variant="body2"
                color={trend.startsWith('+') ? 'success.main' : 'error.main'}
                fontWeight="600"
              >
                {trend}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              vs last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  </Grow>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0, // status 0
    approvedBookings: 0, // status 1
    inProgressBookings: 0, // status 2
    completedBookings: 0, // status 3
    cancelledBookings: 0, // status 4
    refundedBookings: 0, // status 5
    totalRevenue: 0,
    pendingRevenue: 0,
    reportsUploaded: 0,
    upcomingSlots: 0,
    todayBookings: 0,
    thisWeekBookings: 0,
    thisMonthBookings: 0,
    paymentSuccessRate: 0,
  });

  useEffect(() => {
    if (token && isAuthenticated) {
      fetchDashboardData();
    }
  }, [token, isAuthenticated]);

  const fetchDashboardData = async () => {
    if (!token) return;

    try {
      setLoading(true);
      // Fetch all bookings for statistics
      const response = await bookingsService.getBookings(token, {
        limit: 100,
        dashboard: true,
      });

      const allBookings = response.data || [];
      setBookings(allBookings);

      // Calculate statistics based on new booking statuses
      // 0: Pending, 1: Approved, 2: In Progress, 3: Completed, 4: Cancelled, 5: Refunded
      const totalBookings = allBookings.length;
      const pendingBookings = allBookings.filter(b => b.status === 0).length;
      const approvedBookings = allBookings.filter(b => b.status === 1).length;
      const inProgressBookings = allBookings.filter(b => b.status === 2).length;
      const completedBookings = allBookings.filter(b => b.status === 3).length;
      const cancelledBookings = allBookings.filter(b => b.status === 4).length;
      const refundedBookings = allBookings.filter(b => b.status === 5).length;
      const reportsUploaded = allBookings.filter(
        b => b.reportStatus === 1
      ).length;

      // Calculate total revenue from completed bookings with successful payment
      const totalRevenue = allBookings
        .filter(b => b.status === 3 && b.paymentStatus === 1)
        .reduce((sum, b) => sum + parseFloat(b.amount || '0'), 0);

      // Calculate pending revenue (pending, approved, or in progress with unpaid/pending payment)
      const pendingRevenue = allBookings
        .filter(
          b =>
            (b.status === 0 || b.status === 1 || b.status === 2) &&
            (b.paymentStatus === 0 || !b.paymentStatus)
        )
        .reduce((sum, b) => sum + parseFloat(b.amount || '0'), 0);

      // Count upcoming slots (approved or in progress bookings with future dates)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcomingSlots = allBookings.filter(b => {
        if (b.status !== 1 && b.status !== 2) return false;
        const bookingDate = new Date(b.bookingDate);
        return bookingDate >= today;
      }).length;

      // Today's bookings
      const todayEnd = new Date(today);
      todayEnd.setHours(23, 59, 59, 999);
      const todayBookings = allBookings.filter(b => {
        const bookingDate = new Date(b.bookingDate);
        return bookingDate >= today && bookingDate <= todayEnd;
      }).length;

      // This week's bookings
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      const thisWeekBookings = allBookings.filter(b => {
        const bookingDate = new Date(b.bookingDate);
        return bookingDate >= weekStart && bookingDate <= weekEnd;
      }).length;

      // This month's bookings
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      const thisMonthBookings = allBookings.filter(b => {
        const bookingDate = new Date(b.bookingDate);
        return bookingDate >= monthStart && bookingDate <= monthEnd;
      }).length;

      // Payment success rate
      const paidBookings = allBookings.filter(
        b => b.paymentStatus === 1
      ).length;
      const paymentSuccessRate =
        totalBookings > 0 ? (paidBookings / totalBookings) * 100 : 0;

      setStats({
        totalBookings,
        pendingBookings,
        approvedBookings,
        inProgressBookings,
        completedBookings,
        cancelledBookings,
        refundedBookings,
        totalRevenue,
        pendingRevenue,
        reportsUploaded,
        upcomingSlots,
        todayBookings,
        thisWeekBookings,
        thisMonthBookings,
        paymentSuccessRate,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Get upcoming bookings (approved or in progress)
  const upcomingBookings = bookings
    .filter(b => {
      if (b.status !== 1 && b.status !== 2) return false;
      const bookingDate = new Date(b.bookingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return bookingDate >= today;
    })
    .sort(
      (a, b) =>
        new Date(a.bookingDate).getTime() - new Date(b.bookingDate).getTime()
    )
    .slice(0, 5);

  // Get recent completed bookings (status 3)
  const recentCompletedScans = bookings
    .filter(
      (b: Booking) =>
        b.status === 3 && b.reportStatus === 1 && b.paymentStatus === 1
    )
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
    .slice(0, 5);

  const dashboardStats = [
    {
      title: 'Total Bookings',
      value: stats.totalBookings.toString(),
      icon: <Assignment sx={{ fontSize: 28, color: '#6366f1' }} />,
      color: '#6366f1',
    },
    {
      title: 'Pending',
      value: stats.pendingBookings.toString(),
      icon: <PendingActions sx={{ fontSize: 28, color: '#f59e0b' }} />,
      color: '#f59e0b',
    },
    {
      title: 'Approved',
      value: stats.approvedBookings.toString(),
      icon: <CheckCircle sx={{ fontSize: 28, color: '#06b6d4' }} />,
      color: '#06b6d4',
    },
    {
      title: 'In Progress',
      value: stats.inProgressBookings.toString(),
      icon: <TrendingUp sx={{ fontSize: 28, color: '#3b82f6' }} />,
      color: '#3b82f6',
    },
    {
      title: 'Completed',
      value: stats.completedBookings.toString(),
      icon: <MedicalServices sx={{ fontSize: 28, color: '#10b981' }} />,
      color: '#10b981',
    },
    {
      title: 'Cancelled',
      value: stats.cancelledBookings.toString(),
      icon: <Cancel sx={{ fontSize: 28, color: '#ef4444' }} />,
      color: '#ef4444',
    },
    {
      title: 'Refunded',
      value: stats.refundedBookings.toString(),
      icon: <ArrowDownward sx={{ fontSize: 28, color: '#9333ea' }} />,
      color: '#9333ea',
    },
    {
      title: 'Total Revenue',
      value: `${DEFAULT_CURRENCY}${stats.totalRevenue.toFixed(2)}`,
      icon: <AttachMoney sx={{ fontSize: 28, color: '#ec4899' }} />,
      color: '#ec4899',
    },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Fade in timeout={600}>
        <Box mb={4}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Dashboard Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back! Here's what's happening with your business today.
          </Typography>
        </Box>
      </Fade>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <StatCard {...stat} index={index} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Upcoming Slots */}
        <Grid item xs={12} md={6}>
          <Slide direction="right" in timeout={1000}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Typography variant="h6" fontWeight="bold">
                  Upcoming Appointments
                </Typography>
                <IconButton size="small" onClick={() => navigate('/bookings')}>
                  <MoreVert />
                </IconButton>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Booking Ref</TableCell>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {upcomingBookings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No upcoming appointments
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      upcomingBookings.map(booking => (
                        <TableRow
                          key={booking.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            navigate(`/bookings/view/${booking.id}`)
                          }
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              #{booking.bookingRef}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(booking.bookingDate)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatTime(booking.bookingTime)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={BOOKING_STATUS(booking.status).label}
                              color={
                                BOOKING_STATUS(booking.status).color as any
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Slide>
        </Grid>

        {/* Recent Completed Scans */}
        <Grid item xs={12} md={6}>
          <Slide direction="left" in timeout={1000}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Typography variant="h6" fontWeight="bold">
                  Recent Completed Scans
                </Typography>
                <IconButton size="small" onClick={() => navigate('/bookings')}>
                  <MoreVert />
                </IconButton>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Booking Ref</TableCell>
                      <TableCell>Scan Date</TableCell>
                      <TableCell>Report</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentCompletedScans.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No completed scans
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentCompletedScans.map(booking => (
                        <TableRow
                          key={booking.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            navigate(`/bookings/view/${booking.id}`)
                          }
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              #{booking.bookingRef}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(booking.bookingDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={REPORT_STATUS(booking.reportStatus).label}
                              color={
                                REPORT_STATUS(booking.reportStatus).color as any
                              }
                              size="small"
                              icon={<CheckCircle sx={{ fontSize: 16 }} />}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Slide>
        </Grid>
      </Grid>

      {/* Additional Stats Section */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Quick Stats */}
        <Grid item xs={12} md={4}>
          <Fade in timeout={1200}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.02) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <Box
                  sx={{
                    bgcolor: '#6366f115',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                  }}
                >
                  <TrendingUp sx={{ fontSize: 24, color: '#6366f1' }} />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Quick Stats
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Today's Bookings
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {stats.todayBookings}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      (stats.todayBookings / Math.max(stats.totalBookings, 1)) *
                      100
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(99, 102, 241, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#6366f1',
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      This Week
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {stats.thisWeekBookings}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      (stats.thisWeekBookings /
                        Math.max(stats.totalBookings, 1)) *
                      100
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#10b981',
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      This Month
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {stats.thisMonthBookings}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      (stats.thisMonthBookings /
                        Math.max(stats.totalBookings, 1)) *
                      100
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(139, 92, 246, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#8b5cf6',
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Success Rate
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="success.main"
                    >
                      {stats.paymentSuccessRate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.paymentSuccessRate}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#10b981',
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Grid>

        {/* Revenue Analytics */}
        <Grid item xs={12} md={4}>
          <Fade in timeout={1400}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(236, 72, 153, 0.02) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(236, 72, 153, 0.2)',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <Box
                  sx={{
                    bgcolor: '#ec489915',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                  }}
                >
                  <AttachMoney sx={{ fontSize: 24, color: '#ec4899' }} />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Revenue Analytics
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={3}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#10b981">
                    {DEFAULT_CURRENCY}
                    {stats.totalRevenue.toFixed(2)}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Pending Revenue
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#f59e0b">
                    {DEFAULT_CURRENCY}
                    {stats.pendingRevenue.toFixed(2)}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Average Booking Value
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {DEFAULT_CURRENCY}
                      {stats.totalBookings > 0
                        ? (
                            (stats.totalRevenue + stats.pendingRevenue) /
                            stats.totalBookings
                          ).toFixed(2)
                        : '0.00'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Grid>
        {/* Booking Status Breakdown */}
        <Grid item xs={12} md={4}>
          <Fade in timeout={1600}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <Box
                  sx={{
                    bgcolor: '#10b98115',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                  }}
                >
                  <Assignment sx={{ fontSize: 24, color: '#10b981' }} />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Booking Status
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: '#f59e0b15' }}
                    >
                      <PendingActions sx={{ fontSize: 18, color: '#f59e0b' }} />
                    </Avatar>
                    <Typography variant="body2">Pending</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h6" fontWeight="bold">
                      {stats.pendingBookings}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.totalBookings > 0
                        ? (
                            (stats.pendingBookings / stats.totalBookings) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: '#06b6d415' }}
                    >
                      <CheckCircle sx={{ fontSize: 18, color: '#06b6d4' }} />
                    </Avatar>
                    <Typography variant="body2">Approved</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h6" fontWeight="bold">
                      {stats.approvedBookings}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.totalBookings > 0
                        ? (
                            (stats.approvedBookings / stats.totalBookings) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: '#3b82f615' }}
                    >
                      <TrendingUp sx={{ fontSize: 18, color: '#3b82f6' }} />
                    </Avatar>
                    <Typography variant="body2">In Progress</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h6" fontWeight="bold">
                      {stats.inProgressBookings}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.totalBookings > 0
                        ? (
                            (stats.inProgressBookings / stats.totalBookings) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: '#10b98115' }}
                    >
                      <MedicalServices
                        sx={{ fontSize: 18, color: '#10b981' }}
                      />
                    </Avatar>
                    <Typography variant="body2">Completed</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h6" fontWeight="bold">
                      {stats.completedBookings}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.totalBookings > 0
                        ? (
                            (stats.completedBookings / stats.totalBookings) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: '#ef444415' }}
                    >
                      <Cancel sx={{ fontSize: 18, color: '#ef4444' }} />
                    </Avatar>
                    <Typography variant="body2">Cancelled</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h6" fontWeight="bold">
                      {stats.cancelledBookings}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.totalBookings > 0
                        ? (
                            (stats.cancelledBookings / stats.totalBookings) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                </Box>

                {/* <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: '#9333ea15' }}
                      >
                        <ArrowDownward sx={{ fontSize: 18, color: '#9333ea' }} />
                      </Avatar>
                      <Typography variant="body2">Refunded</Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h6" fontWeight="bold">
                        {stats.refundedBookings}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.totalBookings > 0
                          ? (
                              (stats.refundedBookings / stats.totalBookings) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </Typography>
                    </Box>
                  </Box> */}
              </Box>
            </Paper>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
